/**
 * Hōjin — Cookie consent (CNIL/RGPD).
 * Catégories : essentials (toujours actifs), monitoring (Sentry).
 * Persistance localStorage. Émet un event "nanae:consent-change".
 */

export type ConsentCategories = {
  essentials: true; // toujours actifs
  monitoring: boolean;
};

export type ConsentState = {
  decided: boolean;
  categories: ConsentCategories;
  updatedAt: string;
};

const STORAGE_KEY = "nanae.cookie-consent.v1";
export const CONSENT_EVENT = "nanae:consent-change";

const defaultState: ConsentState = {
  decided: false,
  categories: { essentials: true, monitoring: false },
  updatedAt: "",
};

export function getConsent(): ConsentState {
  if (typeof window === "undefined") return defaultState;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return defaultState;
    const parsed = JSON.parse(raw) as Partial<ConsentState>;
    return {
      decided: !!parsed.decided,
      categories: {
        essentials: true,
        monitoring: !!parsed.categories?.monitoring,
      },
      updatedAt: parsed.updatedAt ?? "",
    };
  } catch {
    return defaultState;
  }
}

export function setConsent(categories: Partial<ConsentCategories>): ConsentState {
  const next: ConsentState = {
    decided: true,
    categories: {
      essentials: true,
      monitoring: !!categories.monitoring,
    },
    updatedAt: new Date().toISOString(),
  };
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      window.dispatchEvent(new CustomEvent(CONSENT_EVENT, { detail: next }));
    } catch {
      /* noop */
    }
  }
  return next;
}

export function acceptAll() {
  return setConsent({ monitoring: true });
}

export function rejectAll() {
  return setConsent({ monitoring: false });
}

export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("nanae:open-cookie-preferences"));
}
